import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/layout/Layout';
import api from '../../services/api';
import { Upload, Trash2, FileText, Search, BookOpen, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

const DOCUMENT_TYPES = [
  'Program Guidelines',
  'Institution Contract',
  'MoU',
  'Financial Rules',
  'ATP Rules',
  'SPPRA Rules',
  'Legislative Act',
  'SOP',
  'Policy',
  'Other',
];

const formatBytes = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const KnowledgeBase = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({ title: '', document_type: 'Program Guidelines' });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    try {
      const res = await api.get('/knowledge');
      setDocuments(res.data.documents);
    } catch (err) {
      toast.error('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) return toast.error('Please select a file');
    if (!form.title.trim()) return toast.error('Please enter a document title');

    setUploading(true);
    const data = new FormData();
    data.append('document', selectedFile);
    data.append('title', form.title);
    data.append('document_type', form.document_type);

    try {
      const res = await api.post('/knowledge/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Indexed ${res.data.chunks_indexed} chunks from "${res.data.title}"`);
      setShowUpload(false);
      setForm({ title: '', document_type: 'Program Guidelines' });
      setSelectedFile(null);
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}" from knowledge base?`)) return;
    try {
      await api.delete(`/knowledge/${id}`);
      toast.success('Document deleted');
      setDocuments(prev => prev.filter(d => d.id !== id));
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await api.get(`/knowledge/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data.results);
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  return (
    <Layout title="Knowledge Base">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <div className="bg-blue-900 text-white rounded-xl p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen size={28} />
            <div>
              <h2 className="text-lg font-bold">BBSHRRDB Knowledge Base</h2>
              <p className="text-blue-300 text-xs mt-0.5">
                Upload policy documents, rules & guidelines — the Monitoring Agent will use them to answer your questions
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowUpload(!showUpload)}
            className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-yellow-900 font-semibold px-4 py-2 rounded-lg text-sm transition"
          >
            <Upload size={16} />
            Upload Document
          </button>
        </div>

        {/* Upload Form */}
        {showUpload && (
          <div className="bg-white rounded-xl shadow border border-blue-100 p-6">
            <h3 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
              <Upload size={18} /> Add Document to Knowledge Base
            </h3>
            <form onSubmit={handleUpload} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Title *</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g. BBSHRRDB Program Guidelines 2024"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                  <select
                    value={form.document_type}
                    onChange={e => setForm({ ...form, document_type: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {DOCUMENT_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">File (PDF, DOCX, or TXT — max 20MB)</label>
                <div
                  onClick={() => fileRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 hover:border-blue-400 rounded-lg p-6 text-center cursor-pointer transition"
                >
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-2 text-blue-700">
                      <CheckCircle size={20} />
                      <span className="font-medium text-sm">{selectedFile.name}</span>
                      <span className="text-gray-400 text-xs">({formatBytes(selectedFile.size)})</span>
                    </div>
                  ) : (
                    <div className="text-gray-400 text-sm">
                      <Upload size={24} className="mx-auto mb-2" />
                      Click to select file
                    </div>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.docx,.txt"
                  className="hidden"
                  onChange={e => setSelectedFile(e.target.files[0] || null)}
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex items-center gap-2 bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white px-5 py-2 rounded-lg text-sm font-medium transition"
                >
                  {uploading ? <Loader size={16} className="animate-spin" /> : <Upload size={16} />}
                  {uploading ? 'Processing...' : 'Upload & Index'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowUpload(false); setSelectedFile(null); }}
                  className="px-5 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); if (!e.target.value) setSearchResults(null); }}
                placeholder="Test search — e.g. 'financial rules for procurement' or 'ATP eligibility criteria'"
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              disabled={searching}
              className="bg-blue-800 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition"
            >
              {searching ? <Loader size={14} className="animate-spin" /> : <Search size={14} />}
              Search
            </button>
          </form>

          {searchResults !== null && (
            <div className="mt-4 space-y-3">
              {searchResults.length === 0 ? (
                <div className="flex items-center gap-2 text-amber-600 text-sm bg-amber-50 p-3 rounded-lg">
                  <AlertCircle size={16} />
                  No matching content found. Try different keywords.
                </div>
              ) : (
                searchResults.map((r, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText size={14} className="text-blue-600" />
                      <span className="text-xs font-semibold text-blue-700">{r.document_title}</span>
                      {r.document_type && (
                        <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{r.document_type}</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed line-clamp-4">{r.content}</p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Document List */}
        <div className="bg-white rounded-xl shadow border border-gray-100">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">
              Indexed Documents
              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{documents.length}</span>
            </h3>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-400 flex items-center justify-center gap-2">
              <Loader size={16} className="animate-spin" /> Loading...
            </div>
          ) : documents.length === 0 ? (
            <div className="p-12 text-center">
              <BookOpen size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 text-sm">No documents uploaded yet.</p>
              <p className="text-gray-400 text-xs mt-1">Upload your first document to enable knowledge-based answers in the Monitoring Agent.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {documents.map(doc => (
                <div key={doc.id} className="p-4 flex items-center gap-4 hover:bg-gray-50 transition">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText size={20} className="text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 text-sm truncate">{doc.title}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      {doc.document_type && (
                        <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{doc.document_type}</span>
                      )}
                      <span className="text-xs text-gray-400">{doc.file_name}</span>
                      <span className="text-xs text-gray-400">{formatBytes(doc.file_size)}</span>
                      <span className="text-xs text-green-600">{doc.total_chunks} chunks indexed</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-400">{new Date(doc.created_at).toLocaleDateString('en-PK')}</p>
                    <p className="text-xs text-gray-400">{doc.uploaded_by}</p>
                  </div>
                  <button
                    onClick={() => handleDelete(doc.id, doc.title)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                    title="Delete document"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
          <p className="font-semibold mb-1">How the Knowledge Base works</p>
          <ul className="space-y-1 text-xs text-blue-700 list-disc list-inside">
            <li>Documents are split into text chunks and indexed in the database</li>
            <li>When you ask the Monitoring Agent a question, it automatically searches relevant chunks</li>
            <li>Relevant content is injected into the agent's context so it can answer based on actual documents</li>
            <li>Supported formats: PDF, DOCX, TXT (max 20MB per file)</li>
          </ul>
        </div>

      </div>
    </Layout>
  );
};

export default KnowledgeBase;
